import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import DateTimePicker from '../../../components/ui/DateTimePicker';
import Button from '../../../components/ui/Button';
import { employeeAsync } from '../config';
import { toUtcIso } from '../../../utils/Utils';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 480px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

// Faz D (spec 2026-08-08 §4, Task 4) — Yeni Görüşme: doğrudan-bağlı seçici
// (AsyncGenericSelect, GENEL /v1/employees — bkz. config.buildEmployeeAsync
// yorumu) + tarih/saat. NewCycleModal.js ile AYNI Overlay/Modal/Header/
// Footer iskeleti (desen aynası, task-4-brief.md). `onCreate` başarısızsa
// (BE 403 notDirectManager dahil) modal AÇIK kalır — kullanıcı farklı bir
// çalışan seçip tekrar deneyebilir (NewCycleModal'ın handleSubmit deseniyle
// birebir: id/nesne dönerse kapan, dönmezse saving=false'a düş).
export default function NewMeetingModal({ onClose, onCreate }) {
  const { t } = useTranslation();

  const [employeeId, setEmployeeId] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const valid = useMemo(() => !!employeeId && !!scheduledAt, [employeeId, scheduledAt]);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    const meeting = await onCreate({ employeeId, scheduledAt: toUtcIso(scheduledAt) });
    if (meeting) {
      onClose();
      return;
    }
    setSaving(false);
  }, [onCreate, onClose, employeeId, scheduledAt]);

  return (
    <Overlay onClick={saving ? undefined : onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('oneOnOnes.newModal.title')}</Title>
          <CloseBtn onClick={onClose} disabled={saving}><X size={18} /></CloseBtn>
        </Header>

        <Field>
          <AsyncGenericSelect
            label={t('oneOnOnes.newModal.employeeLabel')}
            value={employeeId}
            onChange={setEmployeeId}
            onSearch={employeeAsync.onSearch}
            onGetById={employeeAsync.onGetById}
            required
          />
        </Field>

        <Field>
          <DateTimePicker
            label={t('oneOnOnes.newModal.scheduledAtLabel')}
            mode="datetime"
            value={scheduledAt}
            onChange={setScheduledAt}
            required
          />
        </Field>

        <Footer>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button data-role="create-meeting-btn" onClick={handleSubmit} disabled={!valid || saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
